/* eslint-disable react/prop-types */
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {
    Image,
    Button,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Stack,
    useMultiStyleConfig,
    useBreakpointValue,
    HStack,
    Box,
    Spinner,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Collapse} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useToast} from './use-toast'
import {remCalc} from '../theme/_utils'
import {useBasketTotalItems} from './use-basket-total-items'
import {findImageGroupBy} from '../utils/image-groups-utils'
import {AddToCartModalCheckIcon} from '../components/icons/shared'
import ProductPrice from '../components/product-price/ProductPrice'
import useProductData from './use-product-data'
import {useBooleanCustomPreference} from '../hooks/use-custom-preference'
import {AddToCartIcon} from '../components/icons'
import {useCurrentBasket} from './use-current-basket'

/**
 * This is the context for managing the AddToCartModal.
 * Used in top level App component.
 */
export const AddToCartModalContext = React.createContext()

/**
 * Renders a single product row inside the AddToCartModal.
 * Defined outside AddToCartModal so its identity is stable across re-renders
 * (defining components inside a render body causes React to remount them every
 * render, which breaks hook initialization and is the root cause of crashes).
 */
const ProductContainer = ({item, currency, notShoppableCountry, officinaShapshot, styles}) => {
    const {product, variant} = item
    const {priceData} = useProductData(product)

    let image
    if (product.c_isOfficina && officinaShapshot) {
        image = {url: officinaShapshot, alt: product.productName}
    } else {
        image = item.isBundle
            ? {url: product.image, alt: product.productName}
            : product.c_image ||
              findImageGroupBy(product.imageGroups, {
                  viewType: 'small',
                  selectedVariationAttributes: product?.variationValues ?? product.variationValues,
              })?.images?.[0]
    }

    return (
        <HStack {...styles.productStack} data-testid="product-added">
            <Stack {...styles.productImageContainer}>
                {image && (
                    <Image {...styles.productImage} src={image.url || image.link} alt={image.alt} />
                )}
            </Stack>
            <Flex {...styles.productDetails}>
                <Flex {...styles.productContentContainer}>
                    <Text {...styles.productHeader}>
                        {variant?.c_variantIdentifier ||
                            product?.c_variantIdentifier ||
                            product?.productName ||
                            product?.name}
                    </Text>
                    {item.isBundle && product.price === 0 ? (
                        <Box {...styles.bundleFreeGiftLabel}>
                            <FormattedMessage
                                defaultMessage="Free Gift"
                                id="bundle_free_gift.text"
                            />
                        </Box>
                    ) : (
                        <ProductPrice
                            priceData={priceData}
                            currency={currency}
                            notShoppableCountry={notShoppableCountry}
                        />
                    )}
                </Flex>
            </Flex>
        </HStack>
    )
}

export const useAddToCartModalContext = () => useContext(AddToCartModalContext)

export const AddToCartModalProvider = ({children}) => {
    const addToCartModal = useAddToCartModal()
    return (
        <AddToCartModalContext.Provider value={addToCartModal}>
            {children}
            <AddToCartModal />
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data} = useAddToCartModalContext()
    const {itemsAdded = [], isSubscription, officinaShapshot} = data || {}
    const {currency} = useCurrency()
    const showToast = useToast()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const {data: basket, refetch: refetchBasket} = useCurrentBasket()
    const isMobile = useBreakpointValue({base: true, lg: false})

    // Refetch on open so the basket cache can't be showing a pre-add snapshot
    useEffect(() => {
        if (isOpen) refetchBasket()
    }, [isOpen])

    // On mobile the modal starts collapsed, showing only the item(s) just
    // added; swiping up expands it to the full cart view. Reset on every new open.
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        if (isOpen) setIsExpanded(false)
    }, [isOpen])

    const showFullCart = !isMobile || isExpanded

    // Put the item(s) just added first, followed by the rest of the basket.
    const addedProductIds = new Set(
        itemsAdded.map(({variant, product}) => variant?.productId || product?.id),
    )

    const cartItems = [...(basket?.productItems ?? [])].sort(
        (a, b) => addedProductIds.has(b.productId) - addedProductIds.has(a.productId),
    )

    const productIds = cartItems.map(({productId}) => productId).join(',')

    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds,
                perPricebook: true,
                expand: ['images', 'prices'],
            },
        },
        {
            enabled: isOpen && Boolean(productIds),
            select: (result) =>
                result?.data?.reduce((productsById, product) => {
                    productsById[product.id] = product
                    return productsById
                }, {}),
        },
    )

    const hasAllProductDetails =
        cartItems.length > 0 && cartItems.every(({productId}) => products?.[productId])

    // Wait for every basket line's product details before rendering, so the
    // "rest of cart" section appears all at once instead of piecing itself together.
    // Not needed while the mobile peek view (added item only) is showing.
    const isLoadingModalItems = showFullCart && Boolean(productIds) && !hasAllProductDetails

    // The item(s) just added are pinned at the top and never re-rendered from a
    // different data source, so expanding never causes them to flash/reflow.
    // The rest of the basket animates in below via Collapse.
    const restCartItems = hasAllProductDetails
        ? cartItems
              .filter(({productId}) => !addedProductIds.has(productId))
              .map((item) => ({
                  ...item,
                  product: products[item.productId],
                  variant: products[item.productId],
              }))
        : []

    const intl = useIntl()
    const styles = useMultiStyleConfig('AddToCartModal')

    const totalItems = useBasketTotalItems()

    const autoCloseTimerRef = useRef(null)
    const modalContentRef = useRef(null)
    const touchStartYRef = useRef(null)

    // Keep a stable ref to onClose so the timer callback never captures a stale closure
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        // the message is displayed if a custom product is added and there are other products in the cart
        if (isOpen && officinaShapshot && totalItems > 0) {
            showToast({
                title: intl.formatMessage({
                    defaultMessage:
                        'Since you add a customized product to the cart, the order cannot include other types of products. Therefore, the cart has been emptied of previous products.',
                    id: 'officina.success.cart_emptied',
                }),
                status: 'success',
            })
        }
    }, [isOpen])

    useEffect(() => {
        // Runs on both platforms; paused while the mobile expanded view is open
        // (mirrors the desktop hover-pause) and resumed once it's collapsed again.
        if (isOpen && !isExpanded) {
            clearTimeout(autoCloseTimerRef.current)
            autoCloseTimerRef.current = setTimeout(() => onCloseRef.current(), 5000)
        }
        return () => clearTimeout(autoCloseTimerRef.current)
    }, [isOpen, isExpanded])

    useEffect(() => {
        if (!isOpen) return
        const handleOutsideClick = (e) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
                clearTimeout(autoCloseTimerRef.current)
                onCloseRef.current()
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [isOpen])

    const handleMouseEnter = () => {
        if (!isMobile) clearTimeout(autoCloseTimerRef.current)
    }

    const handleMouseLeave = () => {
        if (!isMobile) {
            clearTimeout(autoCloseTimerRef.current)
            autoCloseTimerRef.current = setTimeout(() => onCloseRef.current(), 5000)
        }
    }

    const handleTouchStart = (e) => {
        touchStartYRef.current = e.touches[0].clientY
    }

    const handleTouchMove = (e) => {
        if (touchStartYRef.current === null) return
        const delta = e.touches[0].clientY - touchStartYRef.current
        if (delta < -80 && !isExpanded) {
            // Swipe up: expand to show the full cart, pausing the auto-close timer.
            touchStartYRef.current = null
            setIsExpanded(true)
            return
        }
        if (delta > 80) {
            touchStartYRef.current = null
            if (isExpanded) {
                // Swipe down while expanded: collapse back to the peek view;
                // the effect above restarts the timer once isExpanded flips to false.
                setIsExpanded(false)
            } else {
                clearTimeout(autoCloseTimerRef.current)
                onCloseRef.current()
            }
        }
    }

    const handleTouchEnd = () => {
        touchStartYRef.current = null
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="inside"
            motionPreset="none"
            blockScrollOnMount={false}
            trapFocus={false}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={false}
            returnFocusOnClose={false}
        >
            <ModalOverlay {...styles.modalOverlay} />
            <ModalContent
                ref={modalContentRef}
                motionProps={
                    isMobile
                        ? {
                              initial: 'exit',
                              animate: 'enter',
                              exit: 'exit',
                              variants: {
                                  enter: {
                                      opacity: 1,
                                      y: 0,
                                      transition: {duration: 0.35, ease: [0.0, 0.0, 0.2, 1.0]},
                                  },
                                  exit: {
                                      opacity: 0,
                                      y: '100%',
                                      transition: {duration: 0.25, ease: [0.4, 0.0, 1.0, 1.0]},
                                  },
                              },
                          }
                        : {
                              initial: 'exit',
                              animate: 'enter',
                              exit: 'exit',
                              variants: {
                                  enter: {
                                      opacity: 1,
                                      y: 0,
                                      transition: {duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0]},
                                  },
                                  exit: {
                                      opacity: 0,
                                      y: -20,
                                      transition: {duration: 0.2, ease: [0.4, 0.0, 1.0, 1.0]},
                                  },
                              },
                          }
                }
                containerProps={
                    isMobile
                        ? {
                              'data-testid': 'add-to-cart-modal',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              paddingX: '16px',
                              paddingY: '32px',
                              pointerEvents: 'none',
                          }
                        : {
                              'data-testid': 'add-to-cart-modal',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-end',
                              padding: remCalc(20),
                              pointerEvents: 'none',
                          }
                }
                {...styles.modalContent}
                pointerEvents="auto"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchMove={isMobile ? handleTouchMove : undefined}
                onTouchEnd={isMobile ? handleTouchEnd : undefined}
            >
                {isMobile && (
                    <Box data-testid="add-to-cart-modal-drag-handle" {...styles.dragHandle} />
                )}

                <ModalHeader {...styles.modalHeader}>
                    {!isSubscription && <AddToCartModalCheckIcon {...styles.checkIconBox} />}

                    <Text {...styles.modalHeaderTitle}>
                        {isSubscription
                            ? intl.formatMessage(
                                  {
                                      defaultMessage: 'Buy in subscription and <b>save</b>',
                                      id: 'add_to_cart_modal.label.recommendeed_subscriptions',
                                      b: (boldString) => <b>{boldString}</b>,
                                  },
                                  {quantity: totalItems},
                              )
                            : intl.formatMessage(
                                  {
                                      defaultMessage: 'Added item to basket',
                                      id: 'add_to_cart_modal.header',
                                  },
                                  {quantity: totalItems},
                              )}
                    </Text>
                </ModalHeader>

                <ModalBody {...styles.modalBody}>
                    {!isSubscription && (
                        <Flex
                            {...styles.cartProductList}
                            data-testid="cart-product-list"
                            flexDirection="column"
                        >
                            {itemsAdded.map((item, i) => {
                                return (
                                    <ProductContainer
                                        key={item.itemId || item.productId || i}
                                        item={item}
                                        currency={currency}
                                        notShoppableCountry={notShoppableCountry}
                                        officinaShapshot={officinaShapshot}
                                        styles={styles}
                                    />
                                )
                            })}

                            <Collapse
                                in={showFullCart}
                                animateOpacity
                                transition={{
                                    enter: {duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0]},
                                    exit: {duration: 0.2, ease: [0.4, 0.0, 1.0, 1.0]},
                                }}
                            >
                                {isLoadingModalItems ? (
                                    <Flex {...styles.itemsLoadingContainer}>
                                        <Spinner size="md" />
                                    </Flex>
                                ) : (
                                    <Flex {...styles.restCartItemsContainer}>
                                        {restCartItems.map((item) => {
                                            return (
                                                <ProductContainer
                                                    key={item.itemId || item.productId}
                                                    item={item}
                                                    currency={currency}
                                                    notShoppableCountry={notShoppableCountry}
                                                    officinaShapshot={officinaShapshot}
                                                    styles={styles}
                                                />
                                            )
                                        })}
                                    </Flex>
                                )}
                            </Collapse>
                        </Flex>
                    )}
                </ModalBody>

                <ModalFooter {...styles.modalFooter}>
                    <HStack {...styles.itemsInCartContainer}>
                        <Text {...styles.itemsInCartText}>
                            {totalItems === 1
                                ? intl.formatMessage(
                                      {
                                          defaultMessage: '{quantity} item',
                                          id: 'add_to_cart_modal.info.added_to_cart',
                                      },
                                      {quantity: totalItems},
                                  )
                                : intl.formatMessage(
                                      {
                                          defaultMessage: '{quantity} items',
                                          id: 'add_to_cart_modal.info.added_to_cart_items',
                                      },
                                      {quantity: totalItems},
                                  )}
                        </Text>
                        <Text {...styles.itemsInCartText}>
                            {intl.formatMessage({
                                defaultMessage: 'in your basket',
                                id: 'add_to_cart_modal.info.added_to_cart_message',
                            })}
                        </Text>
                    </HStack>

                    <Button
                        as={Link}
                        {...styles.goToCartButton}
                        to="/cart"
                        onClick={onClose}
                        variant="outline-not-rounded"
                        leftIcon={<AddToCartIcon {...styles.goToCartButtonIcon} />}
                    >
                        <Text {...styles.goToCartButtonText}>
                            {intl.formatMessage({
                                id: 'global.go-to-basket',
                                defaultMessage: 'Go to basket',
                            })}
                        </Text>
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array,
    }),
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object,
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any,
}

export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null,
    })

    const {pathname} = useLocation()
    useEffect(() => {
        if (state.isOpen) {
            setState({
                ...state,
                isOpen: false,
            })
        }
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onOpen: (data) => {
            setState({
                isOpen: true,
                data,
            })
        },
        onClose: () => {
            setState({
                isOpen: false,
                data: null,
            })
        },
    }
}
